import type * as Y from 'yjs';
import { useYjsSnapshot } from './use-yjs-snapshot';
import { readSurfaces, SURFACES_ROOT, type SurfaceEntry } from './document-surfaces';

const EMPTY: SurfaceEntry[] = [];

/**
 * A document's surfaces, read reactively from the manifest in its Yjs doc.
 *
 * Takes the already-mounted doc rather than an entity id: `useEntityYjs` mounts a
 * room, and every one of its call sites is a surface mount. This hook is a reader,
 * so callers pass the doc the editor already resolved.
 *
 * Observation is deep because a rename or a reorder writes a field on a nested
 * `Y.Map`, which a shallow observer on the array never reports.
 */
export function useDocumentSurfaces(doc: Y.Doc | null): SurfaceEntry[] {
  return useYjsSnapshot<SurfaceEntry[]>(
    () => (doc ? readSurfaces(doc) : EMPTY),
    (refresh) => {
      if (!doc) return () => {};
      const root = doc.getArray(SURFACES_ROOT);
      root.observeDeep(refresh);
      return () => root.unobserveDeep(refresh);
    },
    [doc],
  );
}
